import { gql } from "@apollo/client";
import { RegisteredVehicle, registeredVehicleFragment } from "./registeredVehicleFragment";
import { Dispute, disputeFragment } from "./disputeFragment";
import { Park, parkFragment } from "common/graphql/fragments/parkFragment";

export const transactionDetailFragment = gql`
  fragment transactionDetailFragment on financial_transaction {
    id
    index
    payerAccountId: account_id
    assignedToAccountAt: assigned_to_account_at
    account {
      id
      organizationName: organization_name
    }
    addressStreet: address_street
    addressCity: address_city
    addressState: address_state
    sessionStartedAt: session_started_at
    sessionStartedAtLocal: session_started_at_local
    sessionEndedAt: session_ended_at
    sessionEndedAtLocal: session_ended_at_local
    park {
      ...parkFragment
    }
    transactionType: transaction_type
    tripFeeCents: trip_fee_cents
    transactionLog: transaction_log_no_balance {
      transactionId: transaction_id
      tripFeeAmount: trip_fee_amount
    }
    status
    settlementTransactionId: settlement_transaction_id
    registeredVehicle: registered_vehicle {
      ...registeredVehicleFragment
    }
    description
    currency
    disputes: transaction_disputes {
      transactionId: transaction_id
      disputeId: dispute_id
      dispute {
        ...disputeFragment
      }
    }
    tags {
      tag: transaction_tag
    }
    citations {
      id
    }
  }
  ${parkFragment}
  ${registeredVehicleFragment}
  ${disputeFragment}
`;

export interface TransactionDetail {
  id: string;
  index: number;
  payerAccountId: string | null;
  /** Stamped when account_id transitions NULL -> NOT NULL (operator assignment, AUT-5031) */
  assignedToAccountAt: string | null;
  /** The account the transaction is billed to (the assigned operator, once assigned) */
  account: { id: string; organizationName: string } | null;
  addressStreet: string;
  addressCity: string;
  addressState: string;
  sessionStartedAt: string;
  sessionStartedAtLocal: string;
  sessionEndedAt: string;
  sessionEndedAtLocal: string;
  park: Park | null;
  doublePark: null;
  transactionType: string;
  /** Flat fee charged for a trip_charge transaction; NULL for every other transaction type */
  tripFeeCents: number | null;
  /** tripFeeAmount is the (negative) trip charge fee, re-derived from permits when tripFeeCents predates the snapshot */
  transactionLog: { transactionId: string; tripFeeAmount: number | null } | null;
  status: string;
  settlementTransactionId: string | null;
  registeredVehicle: RegisteredVehicle | null;
  description: string | null;
  currency: string;
  disputes: { dispute: Dispute }[];
  tags: { tag: TransactionTagValue }[];
  citations: { id: string }[];
}

export type TransactionTagValue = "policy_violation" | "unregistered_vehicle_violation" | "first_park" | "violation";
